import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bus, Calendar, Clock, X, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { bulkRequestBusTastings } from "../lib/api";
import { toast } from "sonner";

/**
 * Multi-stop Bus Tasting creator. Admin/dispatcher picks N
 * dispensaries from the Dispensaries page and drops them into a single
 * tasting day with per-shop time slots. After submit we redirect to
 * the Bus Tastings page so dispatch can assign crew + plan the route.
 */
export function CreateBusTastingDialog({
  open,
  onOpenChange,
  dispensaries,         // [{id, name, city, county}, ...]
  onCreated,            // optional callback after success
}) {
  const navigate = useNavigate();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [requestedDate, setRequestedDate] = useState(today);
  const [sharedNotes, setSharedNotes] = useState("");
  // Map dispensary_id → { time, notes }
  const [perStop, setPerStop] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Reset state when dialog closes so the next open is clean.
  React.useEffect(() => {
    if (!open) {
      setRequestedDate(today);
      setSharedNotes("");
      setPerStop({});
      setSubmitting(false);
      setResult(null);
    }
  }, [open, today]);

  const setStop = (id, patch) => {
    setPerStop((cur) => ({ ...cur, [id]: { ...(cur[id] || {}), ...patch } }));
  };

  const submit = async () => {
    if (!requestedDate) {
      toast.error("Pick a date for the bus event");
      return;
    }
    if (!dispensaries.length) return;
    try {
      setSubmitting(true);
      const res = await bulkRequestBusTastings({
        requested_date: requestedDate,
        shared_notes: sharedNotes.trim(),
        stops: dispensaries.map((d) => ({
          dispensary_id: d.id,
          requested_time: (perStop[d.id]?.time || "").trim() || null,
          requested_notes: (perStop[d.id]?.notes || "").trim(),
        })),
      });
      setResult(res);
      if (res.created_count > 0) {
        toast.success(
          `${res.created_count} bus event${res.created_count === 1 ? "" : "s"} requested for ${requestedDate}`,
          {
            description:
              res.skipped_count > 0
                ? `${res.skipped_count} skipped — see details`
                : "Dispatch can now schedule crew + plan the route.",
          }
        );
      } else {
        toast.error("Nothing created", {
          description: `${res.skipped_count} stop${res.skipped_count === 1 ? "" : "s"} skipped`,
        });
      }
      if (onCreated) onCreated(res);
    } catch (e) {
      toast.error("Bulk request failed", {
        description: e?.response?.data?.detail || e?.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const goToTastings = () => {
    onOpenChange(false);
    // Send dispatcher straight to the Bus Tastings page so they can
    // schedule crew + plan the route.
    navigate("/bus-tastings");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="create-bus-tasting-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bus className="w-5 h-5 text-emerald-400" />
            Create Bus Event
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {dispensaries.length} stop{dispensaries.length === 1 ? "" : "s"} ·
            one shared date, per-shop time slots. Route planning + crew
            assignment happens in the next step on the Bus Events page.
          </DialogDescription>
        </DialogHeader>

        {/* Result panel (shown after submit) */}
        {result ? (
          <div className="space-y-4">
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="flex items-center gap-2 text-emerald-200 font-semibold mb-1">
                <CheckCircle2 className="w-4 h-4" />
                {result.created_count} bus event{result.created_count === 1 ? "" : "s"} requested
              </div>
              <div className="text-xs text-zinc-400">for {result.requested_date}</div>
            </div>
            {result.skipped_count > 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                <div className="flex items-center gap-2 text-amber-200 text-xs font-semibold mb-2 uppercase tracking-wider font-mono">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {result.skipped_count} skipped
                </div>
                <ul className="space-y-1 text-xs text-zinc-300">
                  {result.skipped.map((s) => (
                    <li key={s.dispensary_id} className="flex justify-between gap-2">
                      <span className="truncate">{s.dispensary_name || s.dispensary_id}</span>
                      <span className="text-amber-300/80 shrink-0">{s.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800"
              >
                Done
              </Button>
              <Button
                onClick={goToTastings}
                data-testid="bus-tasting-go-schedule"
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                Go schedule crew + route →
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Shared event-level fields */}
            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  Event date
                </label>
                <Input
                  data-testid="bus-tasting-date"
                  type="date"
                  value={requestedDate}
                  onChange={(e) => setRequestedDate(e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                  Shared notes (optional — applied if a stop has no notes)
                </label>
                <Input
                  data-testid="bus-tasting-shared-notes"
                  value={sharedNotes}
                  onChange={(e) => setSharedNotes(e.target.value)}
                  placeholder="e.g. summer pre-roll promo run"
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
                />
              </div>
            </div>

            {/* Per-stop list */}
            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mb-2">
                <Clock className="w-3 h-3 inline mr-1" />
                Stops & requested time slots
              </div>
              <ul className="space-y-2 max-h-[40vh] overflow-y-auto -mx-1 px-1">
                {dispensaries.map((d, idx) => (
                  <li
                    key={d.id}
                    data-testid={`bus-tasting-stop-${d.id}`}
                    className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3"
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-mono inline-flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-100 truncate">{d.name}</div>
                        <div className="text-[11px] text-zinc-500 truncate">
                          {[d.city, d.county].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-2 ml-8">
                      <Input
                        data-testid={`bus-tasting-time-${d.id}`}
                        value={perStop[d.id]?.time || ""}
                        onChange={(e) => setStop(d.id, { time: e.target.value })}
                        placeholder="10:00 AM"
                        className="bg-zinc-950 border-zinc-800 text-zinc-100 h-8 text-sm font-mono"
                      />
                      <Input
                        data-testid={`bus-tasting-notes-${d.id}`}
                        value={perStop[d.id]?.notes || ""}
                        onChange={(e) => setStop(d.id, { notes: e.target.value })}
                        placeholder="Optional per-stop notes (e.g. ask for John at the counter)"
                        className="bg-zinc-950 border-zinc-800 text-zinc-100 h-8 text-sm"
                      />
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-zinc-500 mt-2 ml-8">
                Times are free-form ("10:00 AM", "between 11 and noon") — the shop's request, not the schedule. Dispatch finalizes timing later.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !requestedDate || !dispensaries.length}
                data-testid="bus-tasting-submit"
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Bus className="w-4 h-4 mr-2" />
                )}
                Request {dispensaries.length} bus event{dispensaries.length === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CreateBusTastingDialog;
